import React, { useEffect, useRef, useState } from "react";
import { TextInput, Toggle } from "@opendate-app/setlist";
import { useMembershipProductNewContext } from "./MembershipProductNewContainer";
import PriceBreakdownTable, { useStagedBreakdown } from "./MembershipPriceBreakdown";
import { checkedFrom, FieldError, ToggleField } from "./formControls";

const toDisplay = (cents) => (cents ? (cents / 100).toFixed(2) : "");
const dollars = (cents) => `$${(cents / 100).toFixed(2)}`;

const PriceInput = ({ id, label, hint, error, cents, onCents }) => {
  const editing = useRef(false);
  const [display, setDisplay] = useState(toDisplay(cents));

  useEffect(() => {
    if (!editing.current) setDisplay(toDisplay(cents));
  }, [cents]);

  return (
    <TextInput
      id={id}
      label={label}
      required
      leadingText="$"
      inputMode="decimal"
      placeholder="0.00"
      hint={hint}
      error={error}
      value={display}
      onChange={(e) => {
        editing.current = true;
        const raw = e.target.value.replace(/[^0-9.]/g, "");
        setDisplay(raw);
        const value = Math.round(parseFloat(raw) * 100);
        onCents(Number.isFinite(value) ? value : 0);
      }}
      onBlur={() => {
        editing.current = false;
        setDisplay(toDisplay(cents));
      }}
    />
  );
};

const CADENCES = [
  {
    key: "annual",
    title: "Annual pricing",
    defaultKey: "default_price",
    ticketingKey: "ticketing_price_record",
    defaultUpdater: "updateDefaultPrice",
    ticketingUpdater: "updateTicketingPrice",
    equivalent: (cents) => `${dollars(cents)}/year = ${dollars(cents / 12)}/month`,
  },
  {
    key: "monthly",
    title: "Monthly pricing",
    defaultKey: "default_monthly_price",
    ticketingKey: "ticketing_monthly_price_record",
    defaultUpdater: "updateDefaultMonthlyPrice",
    ticketingUpdater: "updateTicketingMonthlyPrice",
    equivalent: (cents) => `${dollars(cents)}/month = ${dollars(cents * 12)}/year`,
  },
];

const PricingSection = ({ enabled, setEnabled, errors }) => {
  const context = useMembershipProductNewContext();
  const { membershipProduct, updateMembershipProduct } = context;
  const { staged, loading } = useStagedBreakdown({ annualEnabled: enabled.annual, monthlyEnabled: enabled.monthly });
  const absorbFees = membershipProduct.absorb_fees !== false;
  const cents = (key) => (membershipProduct[key] ? membershipProduct[key].amount_cents : null);

  return (
    <>
      <FieldError>{errors.pricing}</FieldError>
      <div className="membership-price-cards">
        {CADENCES.map((c) => (
          <div className="membership-price-card" key={c.key}>
            <Toggle
              id={`membership-${c.key}-enabled`}
              label={<span className="membership-price-card-title">{c.title}</span>}
              checked={enabled[c.key]}
              onChange={(value) => setEnabled(c.key, checkedFrom(value))}
            />
            {enabled[c.key] ? (
              <>
                <div className="membership-price-inputs">
                  <PriceInput
                    id={`membership-default-${c.key}-price`}
                    label="Default price"
                    cents={cents(c.defaultKey)}
                    error={errors[`default_${c.key}`]}
                    onCents={(value) => context[c.defaultUpdater]("amount_cents", value)}
                  />
                  {membershipProduct.add_to_event_ticketing ? (
                    <PriceInput
                      id={`membership-ticketing-${c.key}-price`}
                      label="Ticketing price"
                      hint="When bought with tickets."
                      cents={cents(c.ticketingKey)}
                      error={errors[`ticketing_${c.key}`]}
                      onCents={(value) => context[c.ticketingUpdater]("amount_cents", value)}
                    />
                  ) : null}
                </div>
                {cents(c.defaultKey) ? <p className="membership-hint">{c.equivalent(cents(c.defaultKey))}</p> : null}
                <PriceBreakdownTable cadence={c.key} staged={staged} loading={loading} />
              </>
            ) : null}
          </div>
        ))}
      </div>
      <ToggleField
        id="membership-absorb-fees"
        title="Absorb fees"
        description={absorbFees ? "Fans pay the listed price and you cover the fees." : "Fans pay the fees on top of the listed price."}
        checked={absorbFees}
        onChange={(checked) => updateMembershipProduct({ absorb_fees: checked })}
      />
    </>
  );
};

export default PricingSection;
